// screens/Reports.js
import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getReports, getDetailedTransactions, setReportFilter, setSelectedClubs, clearSelectedClubs, exportReports } from '../actions/reportAction'
import { listClubs } from '../actions/clubActions'
import { getRegularUsers } from '../actions/userActions'
import { toast } from 'react-toastify'
import moment from 'moment'
import MultiSelectDropdown from '../components/MultiSelectDropdown'
import ScreenHeader from '../components/layout/ScreenHeader'
import {
  ArrowUpRight,
  Building2,
  CalendarCheck,
  CheckCheck,
  Coins,
  FileDown,
  FileSpreadsheet,
  RefreshCw,
} from 'lucide-react'
import {
  BarsChart,
  Button,
  Card,
  ChartCard,
  DataTable,
  DonutChart,
  EmptyState,
  FilterChip,
  FilterItem,
  FilterPanel,
  FilterToggle,
  FormField,
  Money,
  PersonCell,
  SelectInput,
  StatCard,
  StatGrid,
  StatusPill,
  Tabs,
  TextInput,
  useChartColors,
} from '../ui'

// Money leaving the player's wallet; shown as a negative net amount
const OUTFLOW_TYPES = ['cashout', 'buy-in', 'deposit-buyin', 'rake', 'table-charge', 'food-drink', 'membership', 'event-entry']

const AmountCell = ({ value }) =>
  value > 0 ? <Money value={value} className="font-medium" /> : <span className="text-cm-faint">—</span>

const Reports = () => {
  const chartColors = useChartColors()
  const dispatch = useDispatch()

  // Local State
  const [selectedFilter, setSelectedFilter] = useState('today')
  const [customStartDate, setCustomStartDate] = useState('')
  const [customEndDate, setCustomEndDate] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [transactionType, setTransactionType] = useState('all')

  // Redux Selectors
  const { loadingGetReports, getReportsData, errorGetReports } = useSelector(state => state.getReportsInfo)
  const { loadingGetDetailedTransactions, getDetailedTransactionsData, errorGetDetailedTransactions } = useSelector(state => state.getDetailedTransactionsInfo)

  const { clubIds = [] } = useSelector(state => state.selectedClubs)  // Multi-club selection
  const { clubs: availableClubs = [] } = useSelector(state => state.clubList)

  // Filters start open until at least one club is picked
  const [filtersOpen, setFiltersOpen] = useState(() => clubIds.length === 0)
  useEffect(() => {
    if (clubIds.length === 0) setFiltersOpen(true)
  }, [clubIds.length])

  // Filter Options
  const filterOptions = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'this-week', label: 'This Week' },
    { value: 'last-week', label: 'Last Week' },
    { value: 'this-month', label: 'This Month' },
    { value: 'last-month', label: 'Last Month' },
    { value: 'custom', label: 'Custom' }
  ]

  // Transaction Type Options
  const transactionTypeOptions = [
    { value: 'all', label: 'All Transactions' },
    { value: 'deposit', label: 'Top-ups' },
    { value: 'check-in', label: 'Check-ins' },
    { value: 'login', label: 'Logins' }
  ]

  // Effects
  useEffect(() => {
    // Fetch available clubs on mount
    dispatch(listClubs())
  }, [])

  useEffect(() => {
    if (errorGetReports) {
      toast.error(errorGetReports)
    }
  }, [errorGetReports])

  useEffect(() => {
    if (errorGetDetailedTransactions) {
      toast.error(errorGetDetailedTransactions)
    }
  }, [errorGetDetailedTransactions])



  // Refetch reports when club selection changes (multi-club support)
  useEffect(() => {
    if (clubIds.length > 0) {
      handleFetchReports()
    }
  }, [clubIds, selectedFilter])

  // Handle deleted/inactive clubs warning
  useEffect(() => {
    if (getReportsData?.data?.clubIds) {
      const returnedClubIds = getReportsData.data.clubIds
      // Only sync if clubs were actually removed (avoid infinite loop from same arrays with different references)
      const clubsWereRemoved = returnedClubIds.length < clubIds.length && 
        !clubIds.every(id => returnedClubIds.includes(id))
      if (clubsWereRemoved) {
        toast.warning(
          `${clubIds.length - returnedClubIds.length} club(s) are no longer available and were excluded`
        )
        // Sync state with backend
        dispatch(setSelectedClubs(
          returnedClubIds,
          getReportsData.data.clubNames || []
        ))
      }
    }
  }, [getReportsData])

  // Handler Functions
  const handleFetchReports = () => {
    // Validate club selection
    if (clubIds.length === 0) {
      // Don't fetch if no clubs selected (empty state)
      return
    }

    if (selectedFilter === 'custom') {
      if (!customStartDate || !customEndDate) {
        toast.error('Please select both start and end dates for custom filter')
        return
      }
      setCurrentPage(1)
      dispatch(getReports(selectedFilter, customStartDate, customEndDate, clubIds))
      dispatch(getDetailedTransactions(selectedFilter, customStartDate, customEndDate, 1, pageSize, transactionType, clubIds))
    } else {
      setCurrentPage(1)
      dispatch(getReports(selectedFilter, null, null, clubIds))
      dispatch(getDetailedTransactions(selectedFilter, null, null, 1, pageSize, transactionType, clubIds))
    }
  }

  // Handle club selection change
  const handleClubSelectionChange = (selectedIds) => {
    // Map club IDs to club names
    const selectedClubNames = availableClubs
      .filter(club => selectedIds.includes(club._id))
      .map(club => ({ id: club._id, name: club.name, code: club.clubCode }))

    dispatch(setSelectedClubs(selectedIds, selectedClubNames))
  }

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter)
    if (filter !== 'custom' && clubIds.length > 0) {
      setCurrentPage(1)
      dispatch(getReports(filter, null, null, clubIds))
      dispatch(getDetailedTransactions(filter, null, null, 1, pageSize, transactionType, clubIds))
    }
  }

  const handleCustomDateSubmit = () => {
    if (!customStartDate || !customEndDate) {
      toast.error('Please select both start and end dates')
      return
    }
    if (moment(customStartDate).isAfter(moment(customEndDate))) {
      toast.error('Start date cannot be after end date')
      return
    }
    if (clubIds.length === 0) {
      toast.error('Please select at least one club')
      return
    }
    setCurrentPage(1)
    dispatch(getReports('custom', customStartDate, customEndDate, clubIds))
    dispatch(getDetailedTransactions('custom', customStartDate, customEndDate, 1, pageSize, transactionType, clubIds))
  }



  // Export Handler (Excel format)
  const handleExportExcel = () => {
    if (clubIds.length === 0) {
      toast.error('Please select at least one club to export')
      return
    }
    dispatch(exportReports(selectedFilter, customStartDate, customEndDate, 'excel'))
    toast.success('Exporting to Excel - download will begin shortly')
  }

  // Export Handler (PDF format)
  const handleExportPDF = () => {
    if (clubIds.length === 0) {
      toast.error('Please select at least one club to export')
      return
    }
    dispatch(exportReports(selectedFilter, customStartDate, customEndDate, 'pdf'))
    toast.success('Exporting to PDF - download will begin shortly')
  }




  const handlePageChange = (page, size = pageSize) => {
    setCurrentPage(page)
    dispatch(getDetailedTransactions(
      selectedFilter,
      customStartDate,
      customEndDate,
      page,
      size,
      transactionType,
      clubIds
    ))
  }

  const handlePageSizeChange = (size) => {
    setPageSize(size)
    handlePageChange(1, size)
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0)
  }

  const formatDate = (date) => {
    if (!date) return 'N/A'
    return moment(date).format('DD MMM YYYY, HH:mm')
  }

  const handleTransactionTypeChange = (newType) => {
    setTransactionType(newType)
    setCurrentPage(1)
    dispatch(getDetailedTransactions(
      selectedFilter,
      customStartDate,
      customEndDate,
      1,
      pageSize,
      newType,
      clubIds
    ))
  }

  const reportData = getReportsData?.data
  const firstClub = availableClubs.find((club) => club._id === clubIds[0])
  const selectedClubLabel = !clubIds.length
    ? 'No clubs selected'
    : `${firstClub ? `${firstClub.clubCode} - ${firstClub.name}` : '1 club'}${clubIds.length > 1 ? ` +${clubIds.length - 1} more` : ''}`
  const hasClubs = clubIds.length > 0
  const detailed = getDetailedTransactionsData?.data
  const transactionColumns = [
    {
      field: 'serialNo',
      title: 'S.No',
      width: 64,
      render: (t, { rowIndex }) => (
        <span className="text-cm-faint tabular-nums">{rowIndex + 1}</span>
      ),
    },
    {
      field: 'createdAt',
      title: 'Date',
      render: (t) => (
        <div className="whitespace-nowrap">
          <div className="font-medium text-cm-text">{moment(t.createdAt).format('DD MMM YYYY')}</div>
          <div className="text-xs text-cm-muted">
            {t.transactionType === 'daily-summary'
              ? (t.firstLoginTime ? `In ${moment(t.firstLoginTime).format('hh:mm A')}` : '—')
              : moment(t.createdAt).format('hh:mm A')}
          </div>
        </div>
      ),
    },
    {
      field: 'userId.name',
      title: 'Player',
      render: (t) => (
        <PersonCell
          name={t.userId?.name || 'Unknown'}
          sub={[t.userId?.identifier, t.userId?.phoneNumber].filter(Boolean).join(' · ')}
        />
      ),
    },
    {
      field: 'totalDeposit',
      title: 'Top-up',
      align: 'right',
      render: (t) => <AmountCell value={
        t.transactionType === 'daily-summary' ? t.totalDeposit
          : ['deposit', 'chip-deposit'].includes(t.transactionType) ? t.amount : 0
      } />,
    },
    {
      field: 'totalBonus',
      title: 'Bonus',
      align: 'right',
      render: (t) => <AmountCell value={
        t.transactionType === 'daily-summary' ? t.totalBonus : t.bonusAmount
      } />,
    },
    {
      field: 'totalDurationMinutes',
      title: 'Played',
      render: (t) => {
        if (t.transactionType === 'daily-summary' && t.totalDurationMinutes > 0) {
          return <span className="tabular-nums">{Math.floor(t.totalDurationMinutes / 60)}h {t.totalDurationMinutes % 60}m</span>
        }
        if (t.transactionType === 'check-in') {
          return t.duration
            ? <span className="tabular-nums">{Math.floor(t.duration / 60)}h {t.duration % 60}m</span>
            : <StatusPill status="Active" size="sm" />
        }
        return <span className="text-cm-faint">—</span>
      },
    },
    {
      field: 'amount',
      title: 'Net',
      align: 'right',
      render: (t) => {
        if (t.transactionType === 'daily-summary') {
          return t.amount ? <Money value={t.amount} sign className="font-bold" /> : <span className="text-cm-faint">—</span>
        }
        if (['check-in', 'login'].includes(t.transactionType)) return <span className="text-cm-faint">—</span>
        const outflow = OUTFLOW_TYPES.includes(t.transactionType)
        const inflow = ['deposit', 'chip-deposit'].includes(t.transactionType)
        const value = outflow ? -Math.abs(t.amount || 0) : Math.abs(t.amount || 0)
        return inflow || outflow
          ? <Money value={value} sign className="font-bold" />
          : <span className="font-bold tabular-nums">{formatCurrency(t.amount)}</span>
      },
    },
  ]

  return (
    <div className="flex flex-col gap-5 min-w-0">
      <ScreenHeader
        title="Financial reports"
        description="Top-ups for the clubs and dates you pick."
        actions={
          <>
            <FilterToggle
              open={filtersOpen}
              onToggle={() => setFiltersOpen((o) => !o)}
              count={(hasClubs ? 1 : 0) + (selectedFilter !== 'today' ? 1 : 0)}
              controls="report-filters"
            />
            <Button
              variant="secondary"
              icon={RefreshCw}
              loading={loadingGetReports}
              disabled={!hasClubs}
              onClick={handleFetchReports}
            >
              Refresh
            </Button>
            <Button variant="secondary" icon={FileSpreadsheet} disabled={loadingGetReports || !hasClubs} onClick={handleExportExcel}>
              Excel
            </Button>
            <Button variant="secondary" icon={FileDown} disabled={loadingGetReports || !hasClubs} onClick={handleExportPDF}>
              PDF
            </Button>
          </>
        }
      />

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
          {filtersOpen ? (
            <p className="text-[13px] font-semibold text-cm-text m-0 animate-cm-fade-in">Refine your report</p>
          ) : (
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              <FilterChip icon={Building2} index={0} onClick={() => setFiltersOpen(true)}>
                {selectedClubLabel}
              </FilterChip>
              <FilterChip icon={CalendarCheck} index={1} onClick={() => setFiltersOpen(true)}>
                {filterOptions.find((o) => o.value === selectedFilter)?.label || 'Today'}
              </FilterChip>
            </div>
          )}
          {reportData?.dateRange && hasClubs && (
            <p className="flex items-center gap-1.5 text-[13px] text-cm-muted m-0">
              <CalendarCheck size={14} aria-hidden="true" />
              Showing {reportData.dateRange.displayStart || 'N/A'} – {reportData.dateRange.displayEnd || 'N/A'}
              {' · '}{clubIds.length} club{clubIds.length === 1 ? '' : 's'}
            </p>
          )}
        </div>

        <FilterPanel open={filtersOpen} id="report-filters">
          <div className="pt-4">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
              <FilterItem index={0}>
                <FormField label="Clubs" required>
                  <MultiSelectDropdown
                    options={availableClubs.map(club => ({
                      id: club._id,
                      title: `${club.clubCode} - ${club.name}`
                    }))}
                    selectedIds={clubIds}
                    onSelectionChange={handleClubSelectionChange}
                    placeholder="Select clubs to view reports..."
                    maxSelection={25}
                    searchPlaceholder="Search clubs..."
                    showSelectAll={true}
                    disabled={loadingGetReports}
                  />
                </FormField>
              </FilterItem>
              <FilterItem index={1}>
                <FormField label="Period">
                  <Tabs
                    items={filterOptions}
                    value={selectedFilter}
                    onChange={handleFilterChange}
                    ariaLabel="Report period"
                  />
                </FormField>
              </FilterItem>
            </div>

            {selectedFilter === 'custom' && (
              <FilterItem index={2} className="flex flex-wrap items-end gap-2 mt-4">
                <TextInput
                  label="From"
                  type="date"
                  size="sm"
                  className="w-40"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
                <TextInput
                  label="To"
                  type="date"
                  size="sm"
                  className="w-40"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
                <Button size="sm" icon={CalendarCheck} onClick={handleCustomDateSubmit}>
                  Apply
                </Button>
              </FilterItem>
            )}
          </div>
        </FilterPanel>
      </Card>

      {/* Empty State - No Clubs Selected */}
      {!loadingGetReports && !hasClubs && (
        <Card>
          <EmptyState
            icon={Building2}
            title="Pick the clubs to report on"
            description="Choose one or more clubs above to see their top-ups, buy-ins, cash-outs and commission."
            action={
              <Button
                icon={CheckCheck}
                disabled={availableClubs.length === 0}
                onClick={() => handleClubSelectionChange(availableClubs.map(club => club._id))}
              >
                Select all clubs
              </Button>
            }
          />
        </Card>
      )}

      {/* Loading State */}
      {loadingGetReports && hasClubs && (
        <StatGrid cols={2}>
          {Array.from({ length: 2 }).map((_, i) => <StatCard key={i} label=" " loading />)}
        </StatGrid>
      )}

      {/* Summary + charts */}
      {!loadingGetReports && reportData && hasClubs && (
        <>
          {(() => {
            const flows = [
              { name: 'Top-ups', amount: reportData.totalDeposit?.amount || 0, count: reportData.totalDeposit?.count || 0, color: chartColors.positive },
            ]
            const totalCount = flows.reduce((a, f) => a + f.count, 0)
            return (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <ChartCard
                  title="Where did the money move?"
                  description="Total amount by transaction type for the selected period"
                  empty={flows.every((f) => !f.amount)}
                  height={300}
                >
                  <BarsChart
                    money
                    data={flows}
                    xKey="name"
                    series={[{ key: 'amount', name: 'Amount', cellColors: (row) => row.color }]}
                  />
                </ChartCard>
                <ChartCard
                  title="What kind of transactions?"
                  description={`${totalCount.toLocaleString('en-IN')} transactions, split by type`}
                  empty={!totalCount}
                  height="auto"
                >
                  <DonutChart
                    centerLabel="transactions"
                    height={200}
                    data={flows.map((f) => ({ name: f.name, value: f.count, color: f.color }))}
                  />
                </ChartCard>
              </div>
            )
          })()}
        </>
      )}

      {/* Detailed Transactions Report */}
      {detailed?.transactions && hasClubs && (
        <DataTable
          title="Day-by-day transactions"
          description="One row per player per day, or per transaction when filtered by type."
          columns={transactionColumns}
          data={detailed.transactions}
          rowKey="_id"
          loading={loadingGetDetailedTransactions}
          search={false}
          sortable={false}
          pageSize={pageSize}
          total={detailed.pagination?.totalCount ?? detailed.transactions.length}
          page={currentPage - 1}
          onPageChange={(p) => handlePageChange(p + 1)}
          onPageSizeChange={handlePageSizeChange}
          toolbar={
            <SelectInput
              size="sm"
              className="w-44"
              aria-label="Transaction type"
              value={transactionType}
              onChange={(e) => handleTransactionTypeChange(e.target.value)}
              options={transactionTypeOptions}
            />
          }
          emptyTitle="No transactions in this period"
          emptyDescription="Try a wider date range or another transaction type."
        />
      )}
    </div>
  )
}

export default Reports
